"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Loader2, Mic, Square } from "lucide-react";
import { Button } from "@/app/components/ui";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { logger } from "@/app/lib/logger";

const RECORDER_BITS_PER_SECOND = 64_000;

const formatElapsed = (seconds: number) =>
   `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const VOICE_WAVEFORM_BARS = Array.from({ length: 24 }, (_, index) => index);

const VoiceWaveform = ({ analyser }: { analyser: AnalyserNode | null }) => {
   const [barHeights, setBarHeights] = useState<number[]>(() =>
      VOICE_WAVEFORM_BARS.map(() => 3)
   );

   useEffect(() => {
      if (!analyser) return;

      const frequencyData = new Uint8Array(analyser.frequencyBinCount);
      const minVoiceBin = Math.max(
         1,
         Math.floor((80 * analyser.fftSize) / analyser.context.sampleRate)
      );
      const maxVoiceBin = Math.min(
         frequencyData.length - 1,
         Math.ceil((4_000 * analyser.fftSize) / analyser.context.sampleRate)
      );
      const voiceBinCount = maxVoiceBin - minVoiceBin + 1;
      const updateInterval = window.matchMedia("(prefers-reduced-motion: reduce)").matches
         ? 200
         : 33;
      let animationFrameId = 0;
      let lastUpdatedAt = 0;

      const updateWaveform = (timestamp: number) => {
         if (timestamp - lastUpdatedAt >= updateInterval) {
            analyser.getByteFrequencyData(frequencyData);
            setBarHeights(
               VOICE_WAVEFORM_BARS.map((index) => {
                  const bandStart = Math.floor(
                     minVoiceBin + (index / VOICE_WAVEFORM_BARS.length) * voiceBinCount
                  );
                  const bandEnd = Math.max(
                     bandStart + 1,
                     Math.floor(
                        minVoiceBin +
                           ((index + 1) / VOICE_WAVEFORM_BARS.length) * voiceBinCount
                     )
                  );
                  let peak = 0;
                  let total = 0;

                  for (let bin = bandStart; bin < bandEnd; bin += 1) {
                     const level = frequencyData[Math.min(bin, maxVoiceBin)];
                     peak = Math.max(peak, level);
                     total += level;
                  }

                  const average = total / (bandEnd - bandStart);
                  const voiceLevel = (average * 0.6 + peak * 0.4) / 255;
                  return Math.round(3 + Math.pow(voiceLevel, 0.65) * 25);
               })
            );
            lastUpdatedAt = timestamp;
         }
         animationFrameId = requestAnimationFrame(updateWaveform);
      };

      animationFrameId = requestAnimationFrame(updateWaveform);
      return () => cancelAnimationFrame(animationFrameId);
   }, [analyser]);

   return (
      <span
         className="flex h-7 min-w-0 flex-1 items-center justify-between gap-0.5 overflow-hidden"
         aria-hidden="true"
      >
         {barHeights.map((height, index) => (
            <span
               key={index}
               className="w-0.5 shrink-0 rounded-full bg-primary/80 transition-[height,opacity] duration-75 ease-out"
               style={{ height, opacity: Math.min(1, 0.45 + height / 50) }}
            />
         ))}
      </span>
   );
};

export const ChatInputVoiceStatus = ({
   analyser,
   isProcessing
}: {
   analyser: AnalyserNode | null;
   isProcessing: boolean;
}) => {
   const translation = useTranslations("Home");
   const message = translation(
      isProcessing ? "voiceModeProcessing" : "voiceModeListening"
   );

   return (
      <div
         className="flex min-h-14 items-center gap-3 px-4 py-2 text-sm"
         role="status"
         aria-live="polite"
      >
         <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            {isProcessing ? (
               <Loader2 size={18} className="animate-spin" />
            ) : (
               <Mic size={18} className="animate-pulse" />
            )}
         </span>
         <span className="flex min-w-0 flex-1 flex-col gap-1 text-primary">
            <span className="truncate text-xs font-semibold text-primary">{message}</span>
            {!isProcessing && <VoiceWaveform analyser={analyser} />}
         </span>
      </div>
   );
};

export const ChatInputVoice = ({
   startRef,
   onRecordingChange,
   onProcessingChange,
   onTranscript,
   analyserRef
}: {
   startRef: React.MutableRefObject<(() => void) | null>;
   onRecordingChange: (isRecording: boolean) => void;
   onProcessingChange: (isProcessing: boolean) => void;
   onTranscript: (transcript: string) => void;
   analyserRef: React.MutableRefObject<AnalyserNode | null>;
}) => {
   const translation = useTranslations("Home");
   const { transcribeRecordedVoice, setErrorChat, maxRecordingSeconds } =
      useStateHistoryChatPage();
   const [isRecording, setIsRecording] = useState(false);
   const [elapsedSeconds, setElapsedSeconds] = useState(0);
   const recorderRef = useRef<MediaRecorder | null>(null);
   const audioContextRef = useRef<AudioContext | null>(null);

   const closeAudioAnalysis = useCallback(() => {
      analyserRef.current = null;
      const audioContext = audioContextRef.current;
      audioContextRef.current = null;
      if (audioContext && audioContext.state !== "closed") {
         void audioContext
            .close()
            .catch((error) =>
               logger.warn("[VoiceRecorder] Failed to close audio context", error)
            );
      }
   }, [analyserRef]);

   const stop = useCallback(() => {
      recorderRef.current?.state === "recording" && recorderRef.current.stop();
   }, []);

   // Without this the browser keeps showing its recording indicator after the user
   // navigates away.
   useEffect(
      () => () => {
         stop();
         closeAudioAnalysis();
      },
      [stop, closeAudioAnalysis]
   );

   useEffect(() => {
      if (!isRecording) return;

      const timer = setInterval(() => setElapsedSeconds((seconds) => seconds + 1), 1_000);
      return () => clearInterval(timer);
   }, [isRecording]);

   useEffect(() => {
      if (elapsedSeconds >= maxRecordingSeconds) stop();
   }, [elapsedSeconds, maxRecordingSeconds, stop]);

   const start = async () => {
      let stream: MediaStream | null = null;
      try {
         stream = await navigator.mediaDevices.getUserMedia({ audio: true });
         const recorder = new MediaRecorder(stream, {
            audioBitsPerSecond: RECORDER_BITS_PER_SECOND
         });
         const chunks: Blob[] = [];

         if (typeof AudioContext !== "undefined") {
            let audioContext: AudioContext | null = null;
            try {
               audioContext = new AudioContext();
               const analyser = audioContext.createAnalyser();
               analyser.fftSize = 512;
               analyser.minDecibels = -75;
               analyser.maxDecibels = -20;
               analyser.smoothingTimeConstant = 0.5;
               audioContext.createMediaStreamSource(stream).connect(analyser);
               await audioContext.resume();
               audioContextRef.current = audioContext;
               analyserRef.current = analyser;
            } catch (error) {
               if (audioContext && audioContext.state !== "closed") {
                  void audioContext
                     .close()
                     .catch((closeError) =>
                        logger.warn(
                           "[VoiceRecorder] Failed to close unused audio context",
                           closeError
                        )
                     );
               }
               logger.warn("[VoiceRecorder] Live waveform unavailable", error);
            }
         }

         recorder.ondataavailable = (event) => {
            if (event.data.size) chunks.push(event.data);
         };

         recorder.onstop = async () => {
            stream?.getTracks().forEach((track) => track.stop());
            closeAudioAnalysis();
            recorderRef.current = null;
            setIsRecording(false);
            onRecordingChange(false);
            setElapsedSeconds(0);

            if (!chunks.length) return;
            const recording = new Blob(chunks, { type: recorder.mimeType });
            const extension = recorder.mimeType.includes("mp4") ? "m4a" : "webm";
            onProcessingChange(true);

            try {
               const transcript = await transcribeRecordedVoice(
                  new File([recording], `voice-message.${extension}`, {
                     type: recorder.mimeType
                  })
               );

               if (transcript) {
                  onTranscript(transcript);
               } else {
                  setErrorChat(
                     JSON.stringify({
                        code: "VOICE_TRANSCRIPTION_FAILED",
                        message: translation("voiceTranscriptionFailed")
                     })
                  );
               }
            } finally {
               onProcessingChange(false);
            }
         };

         recorderRef.current = recorder;
         setElapsedSeconds(0);
         setIsRecording(true);
         onRecordingChange(true);
         recorder.start();
      } catch (error) {
         stream?.getTracks().forEach((track) => track.stop());
         closeAudioAnalysis();
         logger.warn("[VoiceRecorder] Unable to start recording", error);
         setErrorChat(
            JSON.stringify({
               code: "MIC_PERMISSION_DENIED",
               message: translation("micPermissionDenied")
            })
         );
      }
   };

   useEffect(() => {
      startRef.current = start;
      return () => {
         startRef.current = null;
      };
   });

   if (!isRecording) return null;

   return (
      <Button
         onClick={stop}
         aria-label={translation("stopRecording")}
         title={translation("stopRecording")}
         className="h-10 shrink-0 gap-2 rounded-full bg-destructive px-3 text-destructive-foreground hover:bg-destructive/90"
      >
         <Square size={14} className="fill-current" />
         <span className="text-xs tabular-nums">{formatElapsed(elapsedSeconds)}</span>
      </Button>
   );
};
